import type { StoryRow } from '@/api/stories'

const amount = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4 })
const seconds = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 })
const known = (value: number | null | undefined): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0
const duration = (value: number | null | undefined) => known(value) ? `${seconds.format(value)} s` : 'Unavailable'
const cost = (value: number | null | undefined) => known(value) ? (value > 0 && value < 0.0001 ? '<$0.0001' : amount.format(value)) : 'Unavailable'

export function StoryMetrics({ story }: { story: StoryRow }) {
  const generationNote = {
    completed: 'Includes retries and narration; excludes initial queue.',
    running: 'Elapsed; still generating.',
    queued: story.generation_seconds == null ? 'Waiting to start.' : 'Elapsed; waiting to resume.',
    failed: 'Until failure; includes retries.',
    cancelled: 'Until cancellation.',
    unavailable: 'No complete job timing recorded.',
  }[story.generation_state ?? 'unavailable']
  const storyNote = {
    reported: 'Provider-reported charge.',
    estimated: 'Estimated list price; actual billing may differ.',
    mock: 'Mock generation; no API charge.',
    unavailable: 'No cost recorded.',
  }[story.story_cost_basis ?? 'unavailable']
  const ttsNote = {
    local: 'Local narration; compute and hosting excluded.',
    free: 'Fish free model; no API charge.',
    local_and_free: 'Local and free narration; hosting excluded.',
    unavailable: 'No complete narration cost record.',
  }[story.tts_cost_basis ?? 'unavailable']

  return (
    <section aria-label="Time and cost" className="card p-5">
      <dl className="grid grid-cols-2 gap-x-5 gap-y-5 sm:grid-cols-4">
        <div>
          <dt className="text-sm text-ink-2">Playback duration</dt>
          <dd className="mt-1">
            <span className="text-lg font-semibold tabular-nums text-ink">{duration(story.duration_s ?? story.runtime_s)}</span>
            <p className="mt-1 text-xs text-ink-3">{story.duration_s == null && known(story.runtime_s) ? 'Estimated from the script.' : 'Length of the video.'}</p>
          </dd>
        </div>
        <div>
          <dt className="text-sm text-ink-2">Generation time</dt>
          <dd className="mt-1">
            <span className="text-lg font-semibold tabular-nums text-ink">{duration(story.generation_seconds)}</span>
            <p className="mt-1 text-xs text-ink-3">{generationNote}</p>
          </dd>
        </div>
        <div>
          <dt className="text-sm text-ink-2">Story AI cost</dt>
          <dd className="mt-1">
            <span className="text-lg font-semibold tabular-nums text-ink">{cost(story.story_cost_usd)}</span>
            <p className="mt-1 text-xs text-ink-3">{storyNote}</p>
            {story.story_cost_partial && <p className="mt-1 text-xs text-ink-3">Partial; some calls are unpriced or still running.</p>}
          </dd>
        </div>
        <div>
          <dt className="text-sm text-ink-2">TTS API cost</dt>
          <dd className="mt-1">
            <span className="text-lg font-semibold tabular-nums text-ink">{cost(story.tts_cost_usd)}</span>
            <p className="mt-1 text-xs text-ink-3">{ttsNote}</p>
          </dd>
        </div>
      </dl>
    </section>
  )
}
